import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { PageHero } from "@/components/page-parts";
import {
  CONVEYANCING_LOCATIONS,
  officeForSlug,
} from "@/lib/conveyancing-locations";
import {
  FEES,
  FIRM,
  FIRM_ADDRESS,
  FIRM_POSTAL_ADDRESS,
  MICHAEL_PHONE,
  PENINSULA_ADDRESS,
  PENINSULA_OFFICE,
} from "@/lib/firm";

/**
 * Internal playbook page — noindexed and unlinked from the public navigation.
 * It generates a copy-ready local directory listing for every conveyancing
 * suburb page, so each citation carries identical NAP data, the same tagged
 * landing-page URL and the same tagged enquiry-form link.
 */
export const Route = createFileRoute("/directory-listings")({
  head: () => ({
    meta: [
      { title: fitTitle(`Local directory listings by suburb (internal) | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Internal pack: copy-ready local business directory listings for every Coastside Law conveyancing suburb page, with matching NAP, tagged URLs and enquiry links."),
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Coastside Law — local directory listing pack" },
      { property: "og:description", content: "Internal reference for consistent Coastside Law business details and local listing links." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: DirectoryListingsPage,
});

const BASE = "https://coastsidelaw.com.au";

/**
 * The directories we submit to. `source` becomes utm_source so /analytics can
 * separate the citations from each other and from organic traffic.
 */
const DIRECTORIES: ReadonlyArray<{
  name: string;
  where: string;
  source: string;
  note: string;
}> = [
  {
    name: "Bing Places for Business",
    where: "bingplaces.com",
    source: "bing-places",
    note: "Add the suburb to the service-area field on the existing office listing. Bing feeds Copilot and several AI assistants.",
  },
  {
    name: "Apple Business Connect",
    where: "businessconnect.apple.com",
    source: "apple-maps",
    note: "Service area only — Apple Maps shows the office pin and the suburb as coverage.",
  },
  {
    name: "Yellow Pages Australia",
    where: "yellowpages.com.au",
    source: "yellowpages",
    note: "Free listing. Put the suburb in the areas-served field, never in the business name.",
  },
  {
    name: "True Local",
    where: "truelocal.com.au",
    source: "truelocal",
    note: "Suburb tag on the office listing; the description carries the suburb naturally.",
  },
  {
    name: "Hotfrog Australia",
    where: "hotfrog.com.au",
    source: "hotfrog",
    note: "Free listing, indexed quickly. One paragraph description plus the tagged link.",
  },
  {
    name: "Localsearch",
    where: "localsearch.com.au",
    source: "localsearch",
    note: "Suburb tag plus category. Keep the phone identical or the record splits.",
  },
  {
    name: "Cylex Australia",
    where: "cylex.com.au",
    source: "cylex",
    note: "Low-effort citation that reinforces NAP consistency across the web.",
  },
  {
    name: "StartLocal",
    where: "startlocal.com.au",
    source: "startlocal",
    note: "Suburb-level directory pages; useful for long-tail suburb queries.",
  },
];

/** Chamber and council citation that depends on which office serves the suburb. */
function regionalCitations(peninsula: boolean) {
  return peninsula
    ? [
        {
          name: "Moreton Bay / Redcliffe Peninsula Chamber of Commerce",
          where: "moretonbaychamber.com.au",
          source: "moretonbay-chamber",
          note: "Member profile. Suburb sits in the chamber catchment.",
        },
        {
          name: "Moreton Bay City Council business directory",
          where: "moretonbay.qld.gov.au business listings",
          source: "mbrc-directory",
          note: "Scarborough address, suburb listed as an area served.",
        },
      ]
    : [
        {
          name: "Caloundra Chamber of Commerce",
          where: "caloundrachamber.com.au",
          source: "caloundra-chamber",
          note: "Member profile. Suburb sits in the Caloundra catchment.",
        },
        {
          name: "Sunshine Coast Council business directory",
          where: "sunshinecoast.qld.gov.au business listings",
          source: "scc-directory",
          note: "Golden Beach address, suburb listed as an area served.",
        },
      ];
}

const PENINSULA_SUBURBS = new Set([
  "north-lakes",
  "scarborough",
  "caboolture",
  "margate",
  
]);

function pageUrl(slug: string, source: string) {
  return `${BASE}/conveyancing/${slug}?utm_source=${source}&utm_medium=referral&utm_campaign=directory-${slug}`;
}

function enquiryUrl(slug: string, source: string) {
  return `${pageUrl(slug, source)}&utm_content=enquiry#enquiry`;
}

const CATEGORIES =
  "Primary: Conveyancer. Secondary: Law firm, Estate planning attorney, Elder law attorney, Real estate attorney.";

function description(name: string) {
  return `Coastside Law is a Queensland law practice acting for buyers and sellers in ${name}. Fixed-fee residential conveyancing — ${FEES.sale} incl. GST for a sale and ${FEES.purchase} incl. GST for a purchase — with a free pre-signing contract review. Every file is handled by a solicitor, not a conveyancing clerk, so a ${name} matter that turns into a deceased estate, family transfer or contract dispute stays with the same firm. We also prepare wills, enduring powers of attorney and probate applications.`;
}

const RULES: readonly string[] = [
  `Business name is exactly "${FIRM.name}" in every listing. Never "Coastside Law Conveyancing ${"<suburb>"}" — a keyword-stuffed name is the fastest way to get a listing suppressed and it breaks NAP matching.`,
  "Address is the real office that serves the suburb. A service-area suburb never gets its own address, mailbox or client address in a directory — that is the most common cause of a Google suspension and it puts the office listing at risk too.",
  "Phone, address and name must match this site character for character, including abbreviations and the space in the postcode.",
  "One listing per office, not one per suburb. Suburbs go in the areas-served or service-area field on the existing listing.",
  "Use the tagged landing-page URL as the website field and the tagged enquiry URL wherever a directory offers a second link, quote button or contact link.",
];

/** The firm-level record every directory must match, character for character. */
const MASTER_OFFICES = [
  {
    label: "Golden Beach (principal place of practice)",
    name: FIRM.name,
    address: FIRM_ADDRESS,
    postal: FIRM_POSTAL_ADDRESS,
    phone: FIRM.phone,
    contact: "Erin Wilkinson",
    email: FIRM.email,
    hours: FIRM.hours,
    website: `${BASE}/`,
    areas:
      "Golden Beach, Caloundra, Pelican Waters, Little Mountain, Baringa, Aura, Currimundi, Aroona, Battery Hill, Kings Beach, Moffat Beach, Dicky Beach, Shelly Beach, Wurtulla, Meridan Plains, wider Sunshine Coast",
  },
  {
    label: "Scarborough (additional place of practice, by appointment)",
    name: FIRM.name,
    address: PENINSULA_ADDRESS,
    postal: FIRM_POSTAL_ADDRESS,
    phone: MICHAEL_PHONE,
    contact: "Michael Klein",
    email: FIRM.email,
    hours: FIRM.hours,
    website: `${BASE}/scarborough`,
    areas:
      "Scarborough, Redcliffe, Margate, Woody Point, Clontarf, Kippa-Ring, North Lakes, Deception Bay, Caboolture, wider Moreton Bay",
  },
] as const;

/** Firm-level citations — submitted once per office, before any suburb work. */
const MASTER_DIRECTORIES: ReadonlyArray<{ name: string; where: string; source: string }> = [
  ...DIRECTORIES.map((d) => ({ name: d.name, where: d.where, source: d.source })),
  { name: "Queensland Law Society find-a-solicitor", where: "qls.com.au", source: "qls" },
  { name: "LinkedIn company page", where: "linkedin.com/company", source: "linkedin" },
  { name: "Facebook business page", where: "facebook.com", source: "facebook" },
  { name: "Caloundra Chamber of Commerce", where: "caloundrachamber.com.au", source: "caloundra-chamber" },
  {
    name: "Moreton Bay / Redcliffe Peninsula Chamber",
    where: "moretonbaychamber.com.au",
    source: "moretonbay-chamber",
  },
];

function masterUrl(source: string, office: string) {
  return `${BASE}/?utm_source=${source}&utm_medium=referral&utm_campaign=directory-core&utm_content=${office}`;
}

function masterEnquiryUrl(source: string, office: string) {
  return `${BASE}/contact?utm_source=${source}&utm_medium=referral&utm_campaign=directory-core&utm_content=${office}-enquiry#enquiry`;
}

export default function DirectoryListingsPage() {
  return (
    <>
      <PageHero
        eyebrow="Internal"
        title="Local directory listings"
        lead="The firm's master NAP record first, then a copy-ready citation pack for every conveyancing suburb page. Same name, same address, same phone everywhere — only the areas-served field and the tagged links change."
      />

      <div className="mx-auto max-w-4xl px-6 pt-16">
        <h2 className="font-serif text-3xl text-foreground">
          Master record — must match the Google Business Profile exactly
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          AI assistants cite a business when the same name, address and phone appear on several
          independent sources. Every field below is copied from this website, which is the same data
          in our structured markup. Submit these two records before touching the suburb pack.
        </p>
        <p className="mt-4 rounded-sm border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
          The Google Business Profile name now reads exactly{" "}
          <strong className="text-foreground">Coastside Law</strong> — one word, no suburb, no
          service words. Keep it that way; every citation below is matched against it.
        </p>

        {MASTER_OFFICES.map((o) => (
          <section key={o.label} className="mt-10 rounded-sm border border-border bg-card p-6">
            <h3 className="font-serif text-2xl text-foreground">{o.label}</h3>
            <dl className="mt-4 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[10rem_1fr]">
              {[
                ["Business name", o.name],
                ["Street address", o.address],
                ["Postal address", o.postal],
                ["Phone", o.phone],
                ["Contact", o.contact],
                ["Email", o.email],
                ["Hours", o.hours],
                ["Website", o.website],
                ["Categories", CATEGORIES],
                ["Areas served", o.areas],
              ].map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="font-semibold text-foreground">{k}</dt>
                  <dd className="break-words text-muted-foreground">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Description (paste as one paragraph): Coastside Law is a Queensland law practice at{" "}
              {o.address}, acting for buyers and sellers in fixed-fee residential conveyancing —{" "}
              {FEES.sale} incl. GST for a sale and {FEES.purchase} incl. GST for a purchase — with a
              free pre-signing contract review. We also prepare wills and enduring powers of
              attorney and act for executors through probate and estate administration. Every file
              is handled by a solicitor. {FIRM.membership}.
            </p>

            <h4 className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Where to submit this record
            </h4>
            <ul className="mt-3 space-y-3">
              {MASTER_DIRECTORIES.map((d) => {
                const office = o.address === FIRM_ADDRESS ? "golden-beach" : "scarborough";
                return (
                  <li key={d.name} className="rounded-sm border border-border p-4 text-sm">
                    <p className="font-semibold text-foreground">
                      {d.name}{" "}
                      <span className="font-normal text-muted-foreground">— {d.where}</span>
                    </p>
                    <p className="mt-2 break-all text-muted-foreground">
                      Website field: {masterUrl(d.source, office)}
                    </p>
                    <p className="mt-1 break-all text-muted-foreground">
                      Enquiry / contact link: {masterEnquiryUrl(d.source, office)}
                    </p>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>


      <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
        <h2 className="font-serif text-3xl text-foreground">Before you submit anything</h2>
        <ul className="mt-6 space-y-3">
          {RULES.map((r) => (
            <li
              key={r}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm leading-relaxed text-muted-foreground"
            >
              {r}
            </li>
          ))}
        </ul>

        <p className="mt-6 rounded-sm border border-primary/40 bg-primary/5 p-4 text-sm leading-relaxed text-muted-foreground">
          Every link below carries UTM tracking, so a lead from Hotfrog in Pelican Waters is
          separable from one from Bing Places in Caloundra in{" "}
          <a href="/analytics" className="font-semibold text-primary underline">
            /analytics
          </a>
          . Paste the URLs in full — the tracking lives in the query string. The enquiry link jumps
          straight to the form on the suburb page and emails both Erin and Michael.
        </p>

        {CONVEYANCING_LOCATIONS.map((loc) => {
          const office = officeForSlug(loc.slug);
          const peninsula = PENINSULA_SUBURBS.has(loc.slug);
          const rows = [...DIRECTORIES, ...regionalCitations(peninsula)];
          return (
            <section key={loc.slug} className="mt-16">
              <h2 className="font-serif text-3xl text-foreground">{loc.name}</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Served from the{" "}
                {peninsula ? `${PENINSULA_OFFICE.suburb} office` : `${FIRM.suburb} office`}. Listed
                everywhere as an area served, never as an address.
              </p>

              <div className="mt-5 rounded-sm border border-border bg-card p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Listing fields — paste verbatim
                </p>
                <dl className="mt-4 grid gap-3 text-sm">
                  <Field label="Business name" value={FIRM.name} />
                  <Field
                    label="Address"
                    value={`${office.street}, ${office.suburb} ${office.state} ${office.postcode}`}
                  />
                  <Field label="Phone" value={FIRM.phone} />
                  <Field label="Email" value={FIRM.email} />
                  <Field label="Hours" value={FIRM.hours} />
                  <Field label="Area served" value={[loc.name, ...loc.nearby].join(", ")} />
                  <Field label="Categories" value={CATEGORIES} />
                  <Field label="Website" value={`${BASE}/conveyancing/${loc.slug}`} />
                </dl>
                <p className="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Description
                </p>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {description(loc.name)}
                </p>
              </div>

              <ul className="mt-4 grid gap-3">
                {rows.map((d) => (
                  <li
                    key={d.name}
                    className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
                  >
                    <p className="font-semibold text-foreground">{d.name}</p>
                    <p className="mt-1">
                      {d.where} — {d.note}
                    </p>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em]">
                      Website link
                    </p>
                    <code className="mt-1 block break-all font-mono text-xs">
                      {pageUrl(loc.slug, d.source)}
                    </code>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.12em]">
                      Enquiry / quote link
                    </p>
                    <code className="mt-1 block break-all font-mono text-xs">
                      {enquiryUrl(loc.slug, d.source)}
                    </code>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        <p className="mt-16 rounded-sm border border-border bg-card p-5 text-sm leading-relaxed text-muted-foreground">
          Work through one suburb a week rather than submitting everything at once — a burst of
          identical citations appearing overnight is a spam signal, and spreading them out gives you
          time to fix any directory that renders the address wrongly. The Google Business Profile
          side of this sits on the{" "}
          <a href="/google-business-profile" className="font-semibold text-primary underline">
            profile playbook
          </a>
          .
        </p>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-l-2 border-accent pl-4">
      <dt className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-foreground">{value}</dd>
    </div>
  );
}
